import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Select } from "./ui/Select";
import { Button, ButtonVariant, ButtonSize } from "@/shared/ui/Button";

interface PaginationProps {
  "data-testid": string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalRecords: number;
  rowsPerPage: number;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  rowsPerPageOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  "data-testid": dataTestId,
  currentPage,
  totalPages,
  onPageChange,
  totalRecords,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
}) => {
  const { t } = useTranslation();
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const startRecord = (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, totalRecords);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const half = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage <= half + 1) {
      for (let i = 1; i <= maxPagesToShow; i++) {
        pageNumbers.push(i);
      }

      pageNumbers.push("...");
      pageNumbers.push(totalPages);
    } else if (currentPage >= totalPages - half) {
      pageNumbers.push(1);
      pageNumbers.push("...");
      for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      pageNumbers.push("...");
      for (let i = currentPage - half; i <= currentPage + half; i++) {
        pageNumbers.push(i);
      }

      pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pages = getPageNumbers();

  return (
    <div
          className="flex justify-between items-center py-3 px-2 pr-24"
          data-testid={dataTestId}
        >
      <div className="flex items-center gap-3" data-testid={`${dataTestId}-controls`}>
        <nav
          className="flex items-center gap-1"
          aria-label={t("pagination.label")}
          data-testid={`${dataTestId}-nav`}
        >
          {/* First page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            data-testid={`${dataTestId}-first-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.firstPage")}
          >
            <ChevronsLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
          {/* Previous page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            data-testid={`${dataTestId}-previous-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.previousPage")}
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
          {/* Page numbers */}
          {pages.map((page, index) =>
            typeof page === "string" ? (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-2 text-sm font-medium text-text"
                data-testid={`${dataTestId}-ellipsis-${index}`}
              >
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={currentPage === page ? ButtonVariant.Primary : ButtonVariant.Outline}
                size={ButtonSize.Xs}
                onClick={() => handlePageChange(page)}
                data-testid={`${dataTestId}-page-${page}-button`}
                className={`${page >= 1000 ? 'min-w-8 px-2' : 'w-8'} h-8 p-0`}
                aria-label={t("pagination.page", { pageNumber: page })}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </Button>
            ),
          )}
          {/* Next page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
            data-testid={`${dataTestId}-next-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.nextPage")}
          >
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </Button>
          {/* Last page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            data-testid={`${dataTestId}-last-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.lastPage")}
          >
            <ChevronsRight className="w-4 h-4" aria-hidden="true" />
          </Button>
        </nav>
      </div>

      <div className="flex items-center gap-3">
        <p className="text-sm text-text" data-testid={`${dataTestId}-info`}>
          {t("pagination.showing", {
            start: startRecord.toLocaleString("en-US"),
            end: endRecord.toLocaleString("en-US"),
            total: totalRecords.toLocaleString("en-US"),
          })}
        </p>
        {/* Spec Pass 2 item #53 — label sits BEFORE the dropdown
            (standard pattern: "Results per page: [10▾]" reads
            left-to-right naturally). Was previously dropdown-then-
            label which forced the eye to backtrack. */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-text">{t("pagination.resultsPerPage")}</span>
          <Select
            value={rowsPerPage}
            onChange={onRowsPerPageChange}
            useNative={true}
            className="min-w-[80px]"
            label=""
            aria-label={t("pagination.rowsPerPage")}
            data-testid={`${dataTestId}-rows-per-page-select`}
            direction="up"
            options={rowsPerPageOptions.map((option) => ({
              value: option,
              label: String(option),
            }))}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(Pagination);
